<template>
  <JobsFilterBanner
    :permalink="props.permalink"
    :initial-state="props.state || ''"
    :initial-city="props.city || ''"
    :states-cities="props.statesCities || {}"
  />
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
    <SectionHeader
      :title="titleText"
      class="mb-6"
    />
    <div class="flex">
      <LastUpdatedDate section="jobs" />
    </div>
    <div v-if="topJobs.length > 0" class="flex flex-row w-full items-start gap-6">
      <!-- Left panel: Job list -->
      <div class="w-full lg:w-[400px] lg:flex-shrink-0">
        <JobsList
          :jobs="topJobs"
          :company-permalink="props.permalink"
          :company-display-name="props.company_name"
          :model-value="selectedJob"
          :is-loading="isLoading"
          :error="error"
          :current-page="currentPage"
          :per-page="perPage"
          :show-pagination="false"
          :show-description="true"
          @update:model-value="handleJobSelect"
        />
        <!-- SEO-friendly pagination with <a href> links -->
        <SeoPagination
          v-if="totalPages > 1"
          :current-page="currentPage"
          :total-pages="totalPages"
          :total="totalJobs"
          :per-page="perPage"
          :base-path="currentBasePath"
          :show-results-info="true"
          class="mt-6"
        />
      </div>
      <!-- Right panel: Job details (desktop only) -->
      <div v-if="selectedJob" class="hidden lg:flex flex-1 flex-col bg-white border border-gray-200 rounded-lg sticky top-[180px] my-4">
        <JobsListing
          :job="selectedJob"
          :location="location"
          :keyword="keyword"
          :request-id="requestId"
          :job-index="selectedJobIndex"
          :use-apply-link="true"
          :company-display-name="props.company_name"
        />
      </div>
    </div>
    <div v-else class="flex flex-col items-center justify-center py-12 text-center">
      <Icon
        name="i-heroicons-briefcase"
        class="w-12 h-12 text-gray-400 mb-4"
      />
      <h3 class="text-lg font-medium text-gray-900">
        No jobs found
      </h3>
      <p class="mt-2 text-sm text-gray-500">
        No open positions matching your filters at the moment.
      </p>
    </div>
    <!-- Mobile job detail slideover -->
    <Slideover v-model="isJobPanelOpen" side="bottom">
      <div v-if="selectedJob" class="relative h-full flex flex-col">
        <div
          ref="dragHandleRef"
          class="flex justify-center py-3 cursor-grab active:cursor-grabbing touch-none flex-shrink-0 bg-white rounded-t-3xl"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <div class="w-12 h-1 bg-gray-300 rounded-full" />
        </div>
        <Icon
          name="i-heroicons-x-mark"
          class="absolute top-2 right-2 w-5 h-5 text-gray-900 cursor-pointer z-10"
          aria-label="Close"
          @click="closePanel"
        />
        <div class="flex-1 overflow-y-auto">
          <JobsListing
            :job="selectedJob"
            :location="location"
            :keyword="keyword"
            :request-id="requestId"
            :job-index="selectedJobIndex"
            :use-apply-link="true"
            :company-display-name="props.company_name"
          />
        </div>
      </div>
    </Slideover>
  </div>
</template>

<script setup lang="ts">
import SectionHeader from '../common/SectionHeader.vue'
import LastUpdatedDate from './LastUpdatedDate.vue'
import JobsFilterBanner from './filters/JobsFilterBanner.vue'
import SeoPagination from '../common/SeoPagination.vue'
import { formatLocationForTitle } from '~/utils/formatters'
import { getStateFullName } from '~/utils/states'

const props = defineProps({
  permalink: String,
  location: String,
  keyword: String,
  company_name: String,
  state: String,
  city: String,
  preFetchedData: Object,
  logo_url: String,
  zipcode: String,
  statesCities: Object,
  initialPage: {
    type: Number,
    default: 1
  }
})
const titleText = computed(() => {
  if (props.city && props.state) {
    const cityName = props.city
    const stateFullName = getStateFullName(props.state)
    return `${props.company_name} Jobs in ${cityName}, ${stateFullName}`
  } else if (props.state) {
    const stateFullName = getStateFullName(props.state)
    return `${props.company_name} Jobs in ${stateFullName}`
  }
  return `${props.company_name} Jobs`
})
const route = useRoute()
const perPage = 20
const error = ref(null)
const searchLocation = ref(props.location)
const isLoading = ref(false)
const selectedJob = ref(null)
const selectedJobIndex = ref(-1)
const isJobPanelOpen = ref(false)
const currentPage = ref(props.initialPage || 1)

// Build the current page path for SEO pagination links
const currentBasePath = computed(() => {
  const segments = [`/company/${props.permalink}/jobs`]
  if (props.city && props.state) {
    const citySlug = props.city.toLowerCase().replace(/\s+/g, '-')
    segments.push(`${citySlug}-${props.state.toLowerCase()}`)
  } else if (props.state) {
    const stateFullName = getStateFullName(props.state)
    if (stateFullName) {
      segments.push(stateFullName.toLowerCase().replace(/\s+/g, '-'))
    }
  }
  return segments.join('/')
})

const totalJobs = computed(() => {
  return jobsData.value?.total || jobsData.value?.available || 0
})

const totalPages = computed(() => {
  if (!totalJobs.value) return 1
  return Math.ceil(totalJobs.value / perPage)
})
const keyword = ref(props.keyword || '')
const filterState = ref(props.state || '')
const filterCity = ref(props.city || '')
const requestId = ref(props.preFetchedData?.request_id || '')
const jobsData = ref<any>(
  props.preFetchedData || { jobs: [], total: 0, available: 0 }
)
const { device } = useDevice()
const send_id = computed(() => (route.query.send_id as string) || '')
const fetchJobs = async (params = {}) => {
  const body: Record<string, any> = {
    company_name: props.company_name,
    company_permalink: props.permalink,
    state: filterState.value,
    city: filterCity.value,
    limit: perPage.toString(),
    offset: ((currentPage.value - 1) * perPage).toString(),
    device: device.value,
    send_id: send_id.value,
    ...params
  }
  return await $fetch(
    `/api/jobs/organic/company/search`,
    {
      method: 'POST',
      body
    }
  )
}
const processJobsWithCompanyLogo = (jobs: any[]) => {
  if (!props.logo_url || !jobs?.length) return jobs
  return jobs.map(job => ({
    ...job,
    logo: props.logo_url
  }))
}
const topJobs = computed(() => {
  const jobs = jobsData.value?.jobs || []
  return processJobsWithCompanyLogo(jobs)
})
watch(
  topJobs,
  (newJobs) => {
    if (newJobs.length > 0 && !selectedJob.value) {
      selectedJob.value = newJobs[0]
      const originalJobs = jobsData.value?.jobs || []
      const index = originalJobs.findIndex(j => j.j_id === newJobs[0].j_id)
      selectedJobIndex.value = index >= 0 ? index : 0
    }
  },
  { immediate: true }
)
const isLargeScreen = ref(false)
const dragHandleRef = ref<HTMLElement | null>(null)
const startY = ref(0)
const currentY = ref(0)
const isDragging = ref(false)
const handleTouchStart = (e: TouchEvent) => {
  startY.value = e.touches[0].clientY
  isDragging.value = true
  currentY.value = startY.value
}
const handleTouchMove = (e: TouchEvent) => {
  if (!isDragging.value) return
  currentY.value = e.touches[0].clientY
  const diff = currentY.value - startY.value
  if (diff > 0) {
    const overlay = document.querySelector('[data-drag-overlay]') as HTMLElement
    const dragHandle = e.target as HTMLElement
    const slideoverPanel = dragHandle.closest('.w-screen, [class*="w-full"][class*="h-full"]') as HTMLElement
    if (slideoverPanel) {
      slideoverPanel.style.transform = `translateY(${diff}px)`
      slideoverPanel.style.transition = 'none'
    }
    if (overlay) {
      const opacity = Math.max(0, 1 - (diff / 300))
      overlay.style.opacity = opacity.toString()
      overlay.style.transition = 'none'
    }
  }
}
const handleTouchEnd = (e: TouchEvent) => {
  if (!isDragging.value) return
  isDragging.value = false
  const dragDistance = currentY.value - startY.value
  const overlay = document.querySelector('[data-drag-overlay]') as HTMLElement
  const dragHandle = e.target as HTMLElement
  const slideoverPanel = dragHandle.closest('.w-screen, [class*="w-full"][class*="h-full"]') as HTMLElement
  if (dragDistance > 100) {
    closePanel()
  }
  if (slideoverPanel) {
    slideoverPanel.style.transform = ''
    slideoverPanel.style.transition = ''
  }
  if (overlay) {
    overlay.style.opacity = ''
    overlay.style.transition = ''
  }
  startY.value = 0
  currentY.value = 0
}
onMounted(() => {
  if (!import.meta.client) return
  const checkScreenSize = () => {
    isLargeScreen.value = window.innerWidth >= 1024
  }
  checkScreenSize()
  window.addEventListener('resize', checkScreenSize)
  onUnmounted(() => {
    window.removeEventListener('resize', checkScreenSize)
  })
})
const handleJobSelect = (job) => {
  const originalJobs = jobsData.value?.jobs || []
  const index = originalJobs.findIndex(j => j.j_id === job.j_id)

  // Save scroll position before updating state
  const scrollY = window.scrollY

  selectedJob.value = job
  selectedJobIndex.value = index

  // Restore scroll position after Vue updates
  nextTick(() => {
    window.scrollTo(0, scrollY)
  })

  // Only open slideover on mobile
  if (!isLargeScreen.value) {
    isJobPanelOpen.value = true
  }
}
const closePanel = () => {
  isJobPanelOpen.value = false
}
const handlePageChange = (newPage: number) => {
  currentPage.value = newPage
  refreshJobs()
}

// Watch for route query changes (SEO pagination uses URL)
watch(
  () => route.query.page,
  (newPage) => {
    const page = parseInt(newPage as string) || 1
    if (page !== currentPage.value) {
      currentPage.value = page
      refreshJobs()
    }
  }
)
const refreshJobs = async () => {
  isLoading.value = true
  try {
    const response = await fetchJobs({
      location: searchLocation.value,
      keyword: keyword.value,
      state: filterState.value,
      city: filterCity.value,
      page: currentPage.value
    })
    if (response && 'request_id' in response && response.request_id) {
      requestId.value = response.request_id
    }
    jobsData.value = response
  } catch (e) {
    error.value = e
  } finally {
    isLoading.value = false
  }
}
</script>
